import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Buffer } from 'buffer';
import {loadStartupHome} from '../api/storefrontStartup';
import { slugify } from '../utils/slug';

import ProductSlider from './utils/ProductSlider';
import HomeHeroBanner from './utils/HomeHeroBanner';
import HomeSkeleton from './utils/HomeSkeleton';
import OrganicDivider from './utils/OrganicDivider';
import TraditionalOilLamp from './utils/TraditionalOilLamp';
import './HomeCategoryGrid.css';
import './HomeProductCarousel.css';
import './HomeBanner.css';
import './HomeBannerGrid.css';
import './HomePromotions.css';
import './HomeVideoBanner.css';
import HomeVideoBanner from './utils/HomeVideoBanner';
import FeaturedProductSpotlight from './utils/FeaturedProductSpotlight';
import './HomeIngredients.css';

const SECTION_ALIASES = {
    hero_banner: 'hero_banner',
    hero: 'hero_banner',
    main_banner: 'hero_banner',
    banner: 'banner',
    single_banner: 'banner',
    banner_grid: 'banner_grid',
    product_carousel: 'product_carousel',
    products: 'product_carousel',
    featured_product: 'featured_product',
    category_grid: 'category_grid',
    categories: 'category_grid',
    brand_grid: 'brand_grid',
    brands: 'brand_grid',
    html_block: 'html_block',
    custom_html: 'html_block',
    promotion_section: 'promotion_section',
    promotion_banner: 'promotion_section',
    promotions: 'promotion_section',
    video_banner: 'video_banner',
    video: 'video_banner',
};

class Home extends Component {
    constructor(props) {
        super(props);
        this.state = {
            sections: [],
            loading: true,
        };
    }

    async componentDidMount() {
        this.isMountedHome = true;
        window.scrollTo(0, 0);

        try {
            const response = await loadStartupHome();
            if (!this.isMountedHome) return;
            const payload = response.data || {};
            const data = payload.data || {};
            const version = data.version || {};
            this.setState({
                sections: this.normalizeSections(version.sections || data.sections || []),
                loading: false,
            }, this.loadThemeScript);
        } catch (error) {
            if (!this.isMountedHome) return;
            console.error(error);
            this.setState({ loading: false }, this.loadThemeScript);
        }
    }

    componentWillUnmount() {
        this.isMountedHome = false;
    }

    loadThemeScript = () => {
        const script = document.createElement('script');
        script.async = true;
        script.src = '/assets/js/main.js';
        document.body.appendChild(script);
    }

    normalizeSections(sections) {
        if (!Array.isArray(sections)) {
            return [];
        }

        return sections
            .filter(Boolean)
            .sort((a, b) => Number(a.sort_order || a.position || a.order || 0) - Number(b.sort_order || b.position || b.order || 0));
    }

    getSectionType(section) {
        const rawType = section.type || section.section_type || section.sectionType || section.component_type || section.key || section.slug || '';
        const normalizedType = String(rawType).trim().toLowerCase().replace(/[\s-]+/g, '_');
        return SECTION_ALIASES[normalizedType] || normalizedType;
    }

    getSectionTitle(section, fallback = '') {
        const settings = this.getSettings(section);
        return section.title || section.heading || section.name || section.component_title || (settings && (settings.title || settings.heading)) || fallback;
    }

    getSectionSubtitle(section, fallback = '') {
        const settings = this.getSettings(section);
        return section.subtitle || section.description || (settings && (settings.subtitle || settings.description)) || fallback;
    }

    getSectionLabel(section, fallback = '') {
        const settings = this.getSettings(section);
        return section.button_text || section.cta_text || (settings && (settings.button_text || settings.cta_text || settings.label)) || fallback;
    }

    renderSectionHeading(section, fallbackTitle, fallbackLabel) {
        const title = this.getSectionTitle(section, fallbackTitle);
        const subtitle = this.getSectionSubtitle(section, '');
        const label = this.getSectionLabel(section, fallbackLabel);

        if (!title && !subtitle && !label) {
            return null;
        }

        return (
            <div className="ps-storefront-heading">
                {subtitle ? <p className="ps-storefront-heading__welcome">{subtitle}</p> : null}
                {title ? <h2>{title}</h2> : null}
                <div className="ps-storefront-heading__divider" aria-hidden="true">
                    <img src="/assets/img/about-line.png" alt="" />
                </div>
                {label ? <div className="ps-storefront-heading__label">{label}</div> : null}
            </div>
        );
    }

    getSettings(section) {
        const rawSettings = section.settings_json || section.settings || {};

        if (typeof rawSettings === 'string') {
            try {
                return JSON.parse(rawSettings);
            } catch (error) {
                console.error(error);
                return {};
            }
        }

        return rawSettings || {};
    }

    getSectionToggle(section, name, fallback = true) {
        const value = this.getSettings(section)[name];
        if (value === undefined || value === null) return fallback;
        return value === true || value === 1 || value === '1' || value === 'true';
    }

    getItems(section) {
        const settings = this.getSettings(section);
        const content = section.content || {};
        const candidates = [
            section.items,
            section.component_items,
            section.banners,
            section.products,
            section.categories,
            section.brands,
            section.promotions,
            settings.items,
            settings.banners,
            settings.products,
            settings.categories,
            settings.brands,
            settings.promotions,
            content.items,
            content.banners,
            content.products,
            content.categories,
            content.brands,
            content.promotions,
        ];
        const items = candidates.find(Array.isArray);
        return (items || [])
            .filter(Boolean)
            .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
    }

    getImage(item) {
        return item.image_path ||
            item.image_url ||
            item.image ||
            item.banner_image ||
            item.desktop_image ||
            item.logo ||
            item.logo_url ||
            item.promotion_url ||
            (item.promotion_media && item.promotion_media.url) ||
            '/assets/img/logo-no-image.png';
    }

    getLink(item, fallback = '/') {
        return item.link || item.url || item.button_url || item.cta_url || item.redirect_url || item.target_url || fallback;
    }

    getCopy(item, fallbackTitle = '') {
        return {
            title: item.title ||
                item.image_title ||
                item.name ||
                item.heading ||
                item.product_name ||
                item.category_name ||
                item.final_category_name ||
                item.sub_category_name ||
                item.main_category_name ||
                item.brand_name ||
                (item.promotion_media && item.promotion_media.title) ||
                fallbackTitle,
            subtitle: item.subtitle || item.description || item.short_description || item.tagline || '',
            button: item.button_text || item.cta_text || item.action_label || 'Shop Now',
        };
    }

    normalizeBannerItem(item, index) {
        const copy = this.getCopy(item, `Banner ${index + 1}`);
        return {
            ...item,
            image_path: this.getImage(item),
            image_title: copy.title,
        };
    }

    getProductLink(product) {
        const slug = product.slug || product.product_slug;
        if (slug) {
            return `/product/${encodeURIComponent(slug)}`;
        }

        const name = product.product_name || product.name || 'Product';
        return `/product/${encodeURIComponent(String(product.id || product.product_id || name))}`;
    }

    getCategoryImage(category) {
        return category.category_media_file_url ||
            category.category_media_url ||
            category.media_file_url ||
            category.image_path ||
            category.image_url ||
            category.image ||
            '/assets/img/logo-no-image.png';
    }

    getCategoryIcon(category) {
        return category.category_media_icon_url ||
            category.category_icon_url ||
            category.media_icon_url ||
            category.icon_path ||
            category.icon_url ||
            '';
    }

    renderLinkedMedia(item, className, fallbackTitle) {
        const copy = this.getCopy(item, fallbackTitle);
        const image = this.getImage(item);
        const link = this.getLink(item);
        const isExternal = /^https?:\/\//.test(link);
        const media = <img src={image} alt={copy.title || fallbackTitle} loading="lazy" />;

        return isExternal ? (
            <a className={className} href={link} target="_blank" rel="noopener noreferrer">{media}</a>
        ) : (
            <Link className={className} to={link}>{media}</Link>
        );
    }

    renderHeroBanner(section, key) {
        const settings = this.getSettings(section);
        const items = this.getItems(section);
        const banners = items.map(item => ({...item, image_path:this.getImage(item)}));
        return banners.length > 0 ? <HomeHeroBanner key={key} slides={banners}
            title={this.getSectionTitle(section, '')} subtitle={this.getSectionSubtitle(section, '')}
            width={settings.banner_width || 'full_width'}
            autoplay/> : null;
    }

    renderBanner(section, key) {
        const settings = this.getSettings(section);
        const items = this.getItems(section).map((item, index) => this.normalizeBannerItem(item, index));
        if (items.length === 0) return null;
        const title = String(this.getSectionTitle(section, '')).trim();
        const subtitle = String(this.getSectionSubtitle(section, '')).trim();
        const widthClass = settings.banner_width === 'full_width' ? ' ps-home-banner-section--full-width' : '';
        return (
            <section className={`ps-home-banner-section${widthClass}`} key={key}>
                <div className="container">
                    {(title || subtitle) && <header className="ps-home-banner-section__heading">
                        {subtitle ? <p>{subtitle}</p> : null}
                        {title ? <h2>{title}</h2> : null}
                        <OrganicDivider />
                    </header>}
                    <div className="ps-home-banner-section__list">
                    {items.map((item, index) => {
                    const link = this.getLink(item);
                    const isExternal = /^https?:\/\//.test(link);
                    const image = <span className="ps-home-banner__image"><img src={item.image_path} alt={item.image_title || `Banner ${index + 1}`} loading="lazy" /></span>;

                    return isExternal ? (
                        <a
                            className="ps-home-banner"
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            key={`${item.image_path || 'banner'}-${index}`}
                        >
                            {image}
                        </a>
                    ) : (
                        <Link
                            className="ps-home-banner"
                            to={link}
                            key={`${item.image_path || 'banner'}-${index}`}
                        >
                            {image}
                        </Link>
                    );
                })}
                    </div>
                </div>
            </section>
        );
    }

    renderBannerGrid(section, key) {
        const items = this.getItems(section);
        if (items.length === 0) return null;
        const title = this.getSectionTitle(section, '');
        const subtitle = this.getSectionSubtitle(section, '');

        return (
            <section className="nw-banner-grid-section" key={key}>
                <div className="container">
                    {(title || subtitle) && <header className="nw-banner-grid-section__heading">
                        <span className="nw-banner-grid-section__mark" aria-hidden="true">
                            <TraditionalOilLamp />
                        </span>
                        <div className="nw-banner-grid-section__heading-copy">
                            {subtitle && <p>{subtitle}</p>}
                            {title && <h2>{title}</h2>}
                        </div>
                        <span className="nw-banner-grid-section__heading-rule" aria-hidden="true" />
                    </header>}
                    <div className={`nw-banner-grid nw-banner-grid--${items.length === 1 ? 'single' : items.length === 3 ? 'trio' : 'multiple'}`}>
                        {items.map((item, index) => {
                            const link = this.getLink(item, '');
                            const isExternal = /^https?:\/\//i.test(link);
                            const copy = this.getCopy(item);
                            const action = item.button_text || item.cta_text || item.action_label;
                            const imagePath = this.getImage(item);
                            const content = <>
                                <span className="nw-banner-grid__media"><img src={imagePath} alt={copy.title || `Banner ${index + 1}`} loading="lazy" decoding="async" /></span>
                                {(copy.title || copy.subtitle || (link && action)) && <div className="nw-banner-grid__body">
                                    <div className="nw-banner-grid__copy">
                                        {copy.title && <h3>{copy.title}</h3>}
                                        {copy.subtitle && <p>{copy.subtitle}</p>}
                                    </div>
                                    {link && action && <span className="nw-banner-grid__action">{action}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M4 12h16m-6-6 6 6-6 6"/></svg></span>}
                                </div>}
                            </>;
                            const cardKey = `${imagePath}-${index}`;
                            if (!link) return <article className="nw-banner-grid__card" key={cardKey}>{content}</article>;
                            return isExternal ? <a className="nw-banner-grid__card" key={cardKey} href={link} target="_blank" rel="noopener noreferrer">{content}</a> : <Link className="nw-banner-grid__card" key={cardKey} to={link}>{content}</Link>;
                        })}
                    </div>
                </div>
            </section>
        );
    }

    renderProductCarousel(section, key) {
        const products = this.getItems(section);
        const enabled = (name) => this.getSectionToggle(section, name);
        const hasDetailedProducts = products.some((product) => this.getImage(product) !== '/assets/img/logo-no-image.png' || product.product_name || product.name);

        if (hasDetailedProducts) {
            return (
                <div className="ps-home-product-carousel" key={key}>
                    <ProductSlider products={products} title={this.getSectionTitle(section)} subtitle={this.getSectionSubtitle(section)} headerDecoration={<OrganicDivider />}
                        showPrice={enabled('show_price')} showAction={enabled('show_add_to_cart')}
                        showOfferBadges={enabled('show_badge')} autoScroll={enabled('autoplay')} compactNavigation />
                </div>
            );
        }

        if (products.length === 0) {
            return null;
        }

        return (
            <section className="ps-storefront-section ps-home-product-carousel ps-home-product-carousel--references" key={key}>
                <div className="container">
                    {this.renderSectionHeading(section, 'Products', 'Shop products')}
                    <div className="ps-storefront-reference-grid">
                        {products.map((product, index) => (
                            <Link
                                className="ps-storefront-reference"
                                to={this.getProductLink(product)}
                                key={`${product.product_id || product.id || index}-${index}`}
                            >
                                <span>Product</span>
                                <strong>#{product.product_id || product.id || index + 1}</strong>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    renderFeaturedProduct(section, key) {
        const selectedProducts = this.getItems(section);
        const fallback = section.product || section.featured_product;
        const products = selectedProducts.length ? selectedProducts : (Array.isArray(fallback) ? fallback : fallback ? [fallback] : []);
        if (!products.length) return null;
        const subtitle = this.getSectionSubtitle(section, '');
        const items = products.map(product => {
            const copy = this.getCopy(product, this.getSectionTitle(section, 'Featured product'));
            const description = String(product.short_description || product.product_subtitle || product.tagline || copy.subtitle || '').replace(/<[^>]*>/g, '').trim();
            return {product, productTitle:copy.title, description:description === subtitle ? '' : description,
                image:product.display_image || product.media?.primary_image_url || this.getImage(product),
                href:this.getProductLink(product)};
        });

        return (
            <FeaturedProductSpotlight key={key} items={items}
                title={this.getSectionTitle(section, 'Featured product')} subtitle={subtitle}
                showPrice={this.getSectionToggle(section, 'show_price')}
                showBadge={this.getSectionToggle(section, 'show_badge')}
                showAction={this.getSectionToggle(section, 'show_add_to_cart')}
                autoplay={this.getSectionToggle(section, 'autoplay', false)}
                buttonLabel={this.getSectionLabel(section, 'Explore product')}/>
        );
    }

    renderCategoryGrid(section, key) {
        const categories = this.getItems(section);
        const settings = this.getSettings(section);
        const columns = Math.min(Math.max(Number(settings.columns || 4), 1), 6);
        const sectionTitle = this.getSectionTitle(section, 'Shop by category');
        const titleWords = String(sectionTitle).trim().split(/\s+/);
        const sectionSubtitle = this.getSectionSubtitle(section, '');

        if (categories.length === 0) return null;

        return (
            <section className="ps-storefront-section ps-home-categories" key={key}>
                <div className="container">
                    <header className="ps-home-categories__heading">
                        <div className="ps-home-categories__heading-copy">
                            {sectionSubtitle ? <p className="ps-home-categories__subtitle">{sectionSubtitle}</p> : null}
                            <h2>{titleWords.map((word, index) => (
                                <React.Fragment key={index}>
                                    {index > 0 ? ' ' : ''}
                                    {index === titleWords.length - 2 ? <span className="ps-home-categories__title-accent">{word}</span> : word}
                                </React.Fragment>
                            ))}</h2>
                            <span className="ps-home-categories__ornament" aria-hidden="true">
                                <svg viewBox="0 0 280 48" fill="none" focusable="false">
                                    <path d="M6 24c28-1.5 61-1.5 99 0-38 .7-71 .7-99 0Zm268 0c-28-1.5-61-1.5-99 0 38 .7 71 .7 99 0Z" fill="#c6ac7c" />
                                    <path d="M34 28h58m96 0h58" stroke="#e3d5ba" strokeWidth=".65" strokeLinecap="round" />
                                    <circle cx="140" cy="24" r="20" fill="#f8f2e5" />
                                    <path d="M124 12a20 20 0 1 0 32 0" stroke="#bca06c" strokeWidth=".8" strokeLinecap="round" />
                                    <path d="M125 33a17 17 0 0 0 30 0" stroke="#dfcda8" strokeWidth=".6" strokeLinecap="round" />
                                    <path d="M140 32c-1-8-1-14 5-21" stroke="#506d45" strokeWidth="1.2" strokeLinecap="round" />
                                    <path d="M139 24c-7-1-11-5-10-11 7 0 12 5 10 11Z" fill="#8a9b6b" />
                                    <path d="M142 19c-1-7 3-12 11-13 1 7-3 12-11 13Z" fill="#47684c" />
                                    <path d="m131 16 7 7m6-6 6-8" stroke="#f8f2e5" strokeWidth=".65" strokeLinecap="round" opacity=".7" />
                                    <path d="M139 31c-6-4-8 6 0 7v-7Z" fill="#b18b5c" />
                                    <path d="M141 31c6-4 8 6 0 7v-7Z" fill="#8d6744" />
                                    <path d="m112 22 2 2-2 2-2-2Zm56 0 2 2-2 2-2-2Z" fill="#b99b63" />
                                </svg>
                            </span>
                        </div>
                    </header>
                    <div className="ps-home-categories__grid" style={{ '--storefront-category-columns': columns }}>
                        {categories.map((category, index) => {
                            const categoryDetails = category.category || {};
                            const copy = this.getCopy(categoryDetails.id ? categoryDetails : category, `Category ${index + 1}`);
                            const id = categoryDetails.id || category.final_category_id || category.sub_category_id || category.main_category_id || category.category_id || category.id || '';
                            const title = categoryDetails.name || category.name || copy.title || `Category #${id}`;
                            const slug = categoryDetails.slug || category.slug || title;
                            const link = this.getLink(category, `/category/${slugify(slug)}`);
                            const icon = this.getCategoryIcon(category);
                            return (
                                <Link className="ps-home-category" to={link} key={`${title}-${index}`}>
                                    <div className="ps-home-category__media">
                                        <img src={this.getCategoryImage(category)} alt={title} loading="lazy" />
                                    </div>
                                    <div className="ps-home-category__body">
                                        {icon ? <span className="ps-home-category__icon" aria-hidden="true"><img src={icon} alt="" loading="lazy" /></span> : null}
                                        <div className="ps-home-category__copy">
                                            <h3>{title}</h3>
                                            <span className="ps-home-category__cta">Shop now</span>
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>
        );
    }

    renderBrandGrid(section, key) {
        const brands = this.getItems(section);
        if (brands.length === 0) return null;

        return (
            <section className="ps-storefront-section ps-storefront-grid-section" key={key}>
                <div className="container">
                    {this.renderSectionHeading(section, 'Brands we love', 'Shop by brand')}
                    <div className="ps-storefront-brand-grid">
                        {brands.map((brand, index) => {
                            const copy = this.getCopy(brand, `Brand ${index + 1}`);
                            const code = brand.brand_code || brand.id || copy.title;
                            const link = this.getLink(brand, `/products/brand/${Buffer.from(String(code)).toString('base64')}/${slugify(copy.title)}`);
                            return (
                                <Link className="ps-storefront-brand" to={link} key={`${copy.title}-${index}`}>
                                    <img src={this.getImage(brand)} alt={copy.title} loading="lazy" />
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>
        );
    }

    renderHtmlBlock(section, key) {
        const settings = this.getSettings(section);
        const html = [section.html, section.body, section.content_html, section.content?.html,
            settings.html, settings.body, settings.content_html, settings.content?.html]
            .find(value => typeof value === 'string' && value.trim());
        if (!html) return null;

        return (
            <section className="ps-storefront-section ps-storefront-html" key={key}>
                <div className="container" dangerouslySetInnerHTML={{ __html: html }} />
            </section>
        );
    }

    renderVideoBanner(section, key) {
        const settings = this.getSettings(section);
        const videoUrl = settings.video_url || (settings.video_media && settings.video_media.url);
        const posterUrl = settings.poster_url || (settings.poster_media && settings.poster_media.url);

        if (!videoUrl) return null;

        return (
            <HomeVideoBanner key={`${key}-${videoUrl}-${posterUrl || ''}`} videoUrl={videoUrl} posterUrl={posterUrl} title={this.getSectionTitle(section, 'Video banner')}/>
        );
    }

    renderPromotionSection(section, key) {
        const promotions = this.getItems(section);
        if (promotions.length === 0) return null;
        const title = this.getSectionTitle(section, '');
        const subtitle = this.getSectionSubtitle(section, '');

        return (
            <section className="nw-home-promotions" key={key}>
                <div className="container">
                    {(title || subtitle) && <header className="ps-home-banner-section__heading">
                        {subtitle && <p>{subtitle}</p>}
                        {title && <h2>{title}</h2>}
                        <OrganicDivider />
                    </header>}
                    <div className={`nw-home-promotions__grid${promotions.length === 1 ? ' nw-home-promotions__grid--single' : ''}`}>
                        {promotions.map((promotion, index) => {
                            const copy = this.getCopy(promotion);
                            const href = this.getLink(promotion, '');
                            const hasCopy = Boolean(copy.title || copy.subtitle || href);
                            const action = <>{copy.button}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12h16m-6-6 6 6-6 6"/></svg></>;
                            return (
                                <article className={`nw-home-promotion${!hasCopy ? ' nw-home-promotion--image-only' : ''}`} key={`${copy.title}-${index}`}>
                                    <div className="nw-home-promotion__media"><img src={this.getImage(promotion)} alt={copy.title || `Promotion ${index + 1}`} loading="lazy" decoding="async" /></div>
                                    {hasCopy && <div className="nw-home-promotion__copy">
                                        {copy.title && <h3>{copy.title}</h3>}
                                        {copy.subtitle ? <p>{copy.subtitle}</p> : null}
                                        {href && (/^https?:\/\//i.test(href)
                                            ? <a className="nw-home-promotion__action" href={href} target="_blank" rel="noopener noreferrer">{action}</a>
                                            : <Link className="nw-home-promotion__action" to={href}>{action}</Link>)}
                                    </div>}
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>
        );
    }

    renderSection(section, index) {
        const key = section.id || section.uuid || `${this.getSectionType(section)}-${index}`;

        switch (this.getSectionType(section)) {
        case 'hero_banner':
            return this.renderHeroBanner(section, key);
        case 'banner':
            return this.renderBanner(section, key);
        case 'banner_grid':
            return this.renderBannerGrid(section, key);
        case 'product_carousel':
            return this.renderProductCarousel(section, key);
        case 'featured_product':
            return this.renderFeaturedProduct(section, key);
        case 'category_grid':
            return this.renderCategoryGrid(section, key);
        case 'brand_grid':
            return this.renderBrandGrid(section, key);
        case 'html_block':
            return this.renderHtmlBlock(section, key);
        case 'video_banner':
            return this.renderVideoBanner(section, key);
        case 'promotion_section':
            return this.renderPromotionSection(section, key);
        default:
            return this.renderBannerGrid(section, key) || this.renderHtmlBlock(section, key);
        }
    }

    render() {
        if (this.state.loading) {
            return <HomeSkeleton />;
        }

        return (
            <div className="ps-home ps-home--1">
                <div className="ps-home-ingredients" aria-hidden="true"
                    style={{backgroundImage: "url('/assets/img/home-ingredients-pattern.svg')"}} />
                <div className="ps-home__content">
                    {this.state.sections.map((section, index) => this.renderSection(section, index))}
                </div>
            </div>
        );
    }
}

export default Home;
